import { useState, useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function SignQuotePage() {
  const { quoteNumber } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState("");
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const [copiedField, setCopiedField] = useState(null);

  const ribData = {
    titulaire: "PUB PRO ENSEIGNES",
    iban: "FR76 1695 8000 0175 6832 6900 059",
    bic: "QNTOFRP1XXX",
    banque: "QONTO"
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text.replace(/\s/g, ''));
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  useEffect(() => {
    axios.get(`${API}/quotes/view/${quoteNumber}`)
      .then(res => { setQuote(res.data); setSignerName(res.data.client_name || ""); })
      .catch(err => setError(err.response?.data?.detail || "Devis introuvable"))
      .finally(() => setLoading(false));
  }, [quoteNumber]);

  const initCanvas = useCallback((canvas) => {
    if (!canvas) return;
    canvasRef.current = canvas;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1a1a1a";
    ctx.lineWidth = 2.5;
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const endDraw = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const submitSignature = async () => {
    if (!hasSignature || !signerName.trim()) return;
    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      await axios.post(`${API}/quotes/sign/${quoteNumber}`, {
        signature_base64: signatureData,
        signer_name: signerName.trim()
      });
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la signature");
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#EFFF00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <span className="text-red-400 text-2xl">!</span>
        </div>
        <h1 className="text-white text-xl font-bold mb-2">Devis introuvable</h1>
        <p className="text-[#A3A3A3]">{error}</p>
      </div>
    </div>
  );

  if (signed || quote?.signed) {
    const acompte = ((quote.estimated_price - 500) * 1.2 / 2).toLocaleString();
    return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Success header */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-8 text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-white text-2xl font-bold mb-2">Devis signé !</h1>
          <p className="text-[#A3A3A3] mb-4">
            Merci {quote.client_name}. Votre devis <span className="text-[#EFFF00] font-bold">{quoteNumber}</span> a été signé avec succès.
          </p>
          <div className="bg-[#0A0A0A] rounded-xl p-4 border border-green-500/20 inline-block">
            <p className="text-green-400 text-sm font-bold">Prochaine étape : versement de l'acompte</p>
            <p className="text-white text-lg font-bold mt-1">{acompte}€</p>
            <p className="text-[#666] text-xs">(50% du total TTC)</p>
          </div>
        </div>

        {/* RIB Section */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden mb-6">
          {/* Red Header */}
          <div className="bg-gradient-to-b from-red-600 to-red-700 p-6 text-center">
            <h2 className="text-white text-xl font-black tracking-wide">PUB PRO ENSEIGNES</h2>
            <p className="text-red-200 text-sm mt-1">Coordonnées bancaires (RIB)</p>
          </div>
          
          {/* RIB Details */}
          <div className="divide-y divide-[#1a1a1a]">
            {[
              { label: "Titulaire", value: ribData.titulaire, field: "titulaire" },
              { label: "IBAN", value: ribData.iban, field: "iban" },
              { label: "BIC / SWIFT", value: ribData.bic, field: "bic" },
              { label: "Banque", value: ribData.banque, field: "banque" },
            ].map(({ label, value, field }) => (
              <div key={field} className="flex items-center justify-between px-6 py-4">
                <div className="flex-shrink-0 w-28">
                  <p className="text-[#A3A3A3] text-sm italic">{label}</p>
                </div>
                <div className="flex-1 mx-4">
                  <p className="text-white font-bold text-sm sm:text-base font-mono tracking-wide">{value}</p>
                </div>
                <button
                  onClick={() => copyToClipboard(value, field)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-xs transition-all ${
                    copiedField === field
                      ? 'bg-green-500 text-white'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                  data-testid={`copy-${field}`}
                >
                  {copiedField === field ? "Copié !" : "Copier"}
                </button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#1a1a1a] text-center">
            <p className="text-[#666] text-xs italic">
              Merci d'indiquer votre nom ou le numéro de devis ({quoteNumber}) en objet du virement.
            </p>
            <p className="text-[#555] text-xs mt-1">
              Pub Pro Enseignes — 07 61 22 28 43
            </p>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center">
          <a
            href={`https://wa.me/33643693688?text=Bonjour, je viens de signer le devis ${quoteNumber} et je souhaite procéder au virement de l'acompte.`}
            className="inline-block bg-[#25D366] text-white px-8 py-3.5 rounded-full font-bold text-sm hover:bg-[#20bd5a] transition-colors"
          >
            Confirmer par WhatsApp
          </a>
          <p className="text-[#444] text-xs mt-6">
            PUB PRO — 06 43 69 36 88 — pubpro.france@gmail.com — simulation-enseigne.fr
          </p>
        </div>
      </div>
    </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-[#EFFF00] text-3xl font-black tracking-tight">PUB PRO</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Enseignes Lumineuses</p>
        </div>

        {/* Quote Summary */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white text-lg font-bold">Devis {quoteNumber}</h2>
            <span className="bg-[#EFFF00]/10 text-[#EFFF00] px-3 py-1 rounded-full text-xs font-bold">
              En attente de signature
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-[#0A0A0A] rounded-xl p-4">
              <p className="text-[#666] text-xs">Client</p>
              <p className="text-white font-bold">{quote.client_name}</p>
            </div>
            <div className="bg-[#0A0A0A] rounded-xl p-4">
              <p className="text-[#666] text-xs">Date du devis</p>
              <p className="text-white font-bold">{quote.quote_date}</p>
            </div>
            <div className="bg-[#0A0A0A] rounded-xl p-4">
              <p className="text-[#666] text-xs">Produit</p>
              <p className="text-white font-bold text-sm">{quote.product_type_label}</p>
            </div>
            <div className="bg-[#0A0A0A] rounded-xl p-4">
              <p className="text-[#666] text-xs">Validité</p>
              <p className="text-white font-bold">{quote.validity_date}</p>
            </div>
          </div>

          <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#EFFF00]/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#666] text-xs">Prix HT (remise -500€ incluse)</p>
                <p className="text-[#EFFF00] font-bold text-2xl">{(quote.estimated_price - 500).toLocaleString()}€</p>
              </div>
              <div className="text-right">
                <p className="text-[#666] text-xs">Total TTC</p>
                <p className="text-white font-bold text-2xl">{((quote.estimated_price - 500) * 1.2).toLocaleString()}€</p>
              </div>
            </div>
            <p className="text-[#666] text-xs mt-2">Acompte 50% à la commande : {(((quote.estimated_price - 500) * 1.2) / 2).toLocaleString()}€</p>
          </div>

          {/* Download PDF */}
          <a
            href={`${API}/quotes/download/${quoteNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center mt-4 text-blue-400 hover:text-blue-300 text-sm underline"
            data-testid="download-quote-link"
          >
            Télécharger le devis complet (PDF)
          </a>
        </div>

        {/* Signature Section */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <h3 className="text-white font-bold text-lg mb-4">Signer ce devis</h3>
          <p className="text-[#A3A3A3] text-sm mb-6">
            En signant ce devis, vous acceptez les conditions de vente mentionnées dans le document.
            Un acompte de 50% sera demandé pour confirmer la commande.
          </p>

          {/* Signer name */}
          <div className="mb-4">
            <label className="block text-[#A3A3A3] text-sm mb-2">Nom complet du signataire</label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] rounded-xl px-4 py-3 text-white focus:border-[#EFFF00] focus:outline-none"
              placeholder="Votre nom et prénom"
              data-testid="signer-name-input"
            />
          </div>

          {/* Signature pad */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[#A3A3A3] text-sm">Votre signature</label>
              {hasSignature && (
                <button
                  onClick={clearSignature}
                  className="text-red-400 text-xs hover:text-red-300"
                  data-testid="clear-signature-btn"
                >
                  Effacer
                </button>
              )}
            </div>
            <div className="bg-white rounded-xl overflow-hidden border-2 border-[#262626] relative" style={{touchAction: 'none'}}>
              <canvas
                ref={initCanvas}
                className="w-full cursor-crosshair"
                style={{height: '180px'}}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
                data-testid="signature-canvas"
              />
              {!hasSignature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-gray-300 text-sm">Dessinez votre signature ici</p>
                </div>
              )}
            </div>
          </div>

          {/* Accept checkbox info */}
          <p className="text-[#666] text-xs mb-6">
            En cliquant "Signer et accepter", je reconnais avoir pris connaissance du devis et en accepte les termes et conditions. 
            Cette signature a valeur d'engagement contractuel.
          </p>

          {/* Sign button */}
          <Button
            onClick={submitSignature}
            disabled={signing || !hasSignature || !signerName.trim()}
            className="w-full bg-green-500 hover:bg-green-600 text-white text-lg py-6 rounded-xl font-bold disabled:opacity-50"
            data-testid="sign-submit-btn"
          >
            {signing ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Signature en cours...
              </span>
            ) : (
              "Signer et accepter le devis"
            )}
          </Button>
        </div>

        {/* Footer */}
        <p className="text-center text-[#444] text-xs mt-8">
          PUB PRO — 06 43 69 36 88 — pubpro.france@gmail.com — simulation-enseigne.fr
        </p>
      </div>
    </div>
  );
}
